import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  Banner,
  TextContainer,
  Text,
  Badge,
  DataTable,
  Modal,
  Select,
  TextField,
  Pagination,
  EmptyState,
  ButtonGroup,
  Tooltip,
  Tabs
} from '@shopify/polaris';
import { SearchMinor, FilterMinor, ViewMinor } from '@shopify/polaris-icons';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';

interface TranslationHistoryEntry {
  id: number;
  job_id: number;
  resource_id: string;
  resource_type: string;
  translation_key: string;
  original_text: string;
  translated_text: string;
  locale: string;
  created_at: string;
  job_status: string;
  job_created_at: string;
}

interface TranslationJob {
  id: number;
  status: string;
  target_locale: string;
  resource_type: string;
  created_at: string;
}

const AVAILABLE_LANGUAGES = [
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Spanish', value: 'es' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Polish', value: 'pl' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Finnish', value: 'fi' },
  { label: 'Danish', value: 'da' },
  { label: 'Norwegian', value: 'no' }
];

const RESOURCE_TYPES = [
  { label: 'Products', value: 'PRODUCT' },
  { label: 'Collections', value: 'COLLECTION' },
  { label: 'Pages', value: 'ONLINE_STORE_PAGE' },
  { label: 'Blog Posts', value: 'ONLINE_STORE_ARTICLE' },
  { label: 'Blogs', value: 'ONLINE_STORE_BLOG' },
  { label: 'Menus', value: 'ONLINE_STORE_MENU' },
  { label: 'Metafields', value: 'METAFIELD' },
  { label: 'Metaobjects', value: 'METAOBJECT' },
  { label: 'Product Options', value: 'PRODUCT_OPTION' },
  { label: 'Product Option Values', value: 'PRODUCT_OPTION_VALUE' },
  { label: 'Selling Plans', value: 'SELLING_PLAN' },
  { label: 'Selling Plan Groups', value: 'SELLING_PLAN_GROUP' },
  { label: 'Shop Policies', value: 'SHOP_POLICY' },
  { label: 'Payment Gateways', value: 'PAYMENT_GATEWAY' },
  { label: 'Delivery Methods', value: 'DELIVERY_METHOD_DEFINITION' },
  { label: 'Packing Slip Templates', value: 'PACKING_SLIP_TEMPLATE' },
  { label: 'Email Templates', value: 'EMAIL_TEMPLATE' },
  { label: 'Filters', value: 'FILTER' },
  { label: 'Links', value: 'LINK' },
  { label: 'Theme', value: 'ONLINE_STORE_THEME' }
];

export const TranslationHistory: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [history, setHistory] = useState<TranslationHistoryEntry[]>([]);
  const [jobs, setJobs] = useState<TranslationJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Filtering state
  const [filters, setFilters] = useState({
    job_id: '',
    resource_type: '',
    locale: '',
    search: ''
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(20);
  
  // Modal state
  const [selectedEntry, setSelectedEntry] = useState<TranslationHistoryEntry | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Tab state
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      loadHistory();
      loadJobs();
    }
  }, [isAuthenticated, currentPage, filters]);

  const loadHistory = async () => {
    setError(null);
    
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const params: any = {
        limit: itemsPerPage,
        offset
      };
      
      if (filters.job_id) params.job_id = parseInt(filters.job_id);
      if (filters.resource_type) params.resource_type = filters.resource_type;
      if (filters.locale) params.locale = filters.locale;
      
      const response = await apiService.getTranslationHistory(params);
      
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setHistory(response.data.history);
        setTotalItems(response.data.total);
      }
    } catch (err) {
      setError('Failed to load translation history');
      console.error('Error loading history:', err);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await apiService.getTranslationJobs();
      if (response.data) {
        const responseData = response.data as any;
        setJobs(responseData.jobs || []);
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
    }
  };

  const getLanguageLabel = (value: string) => {
    const language = AVAILABLE_LANGUAGES.find(lang => lang.value === value);
    return language ? language.label : value;
  };

  const getResourceTypeLabel = (value: string) => {
    const resource = RESOURCE_TYPES.find(res => res.value === value);
    return resource ? resource.label : value;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': { tone: 'warning' as const, text: 'Pending' },
      'in_progress': { tone: 'info' as const, text: 'In Progress' },
      'completed': { tone: 'success' as const, text: 'Completed' },
      'failed': { tone: 'critical' as const, text: 'Failed' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || { tone: 'subdued' as const, text: status };
    return <Badge tone={config.tone}>{config.text}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      job_id: '',
      resource_type: '',
      locale: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const filteredHistory = history.filter(entry => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        entry.original_text.toLowerCase().includes(searchLower) ||
        entry.translated_text.toLowerCase().includes(searchLower) ||
        entry.translation_key.toLowerCase().includes(searchLower) ||
        entry.resource_id.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const tableRows = filteredHistory.map(entry => [
    <Text variant="bodyMd" fontWeight="semibold" as="span" key={`${entry.id}-job`}>
      Job #{entry.job_id}
    </Text>,
    <div key={`${entry.id}-resource`}>
      <Text variant="bodyMd" as="p" fontWeight="semibold">
        {getResourceTypeLabel(entry.resource_type)}
      </Text>
      <Text variant="bodySm" as="p" tone="subdued">
        ID: {entry.resource_id}
      </Text>
    </div>,
    <div key={`${entry.id}-translation`}>
      <Text variant="bodyMd" as="p" fontWeight="semibold">
        {entry.translation_key}
      </Text>
      <Text variant="bodySm" as="p" tone="subdued">
        {truncateText(entry.original_text, 40)}
      </Text>
    </div>,
    <div key={`${entry.id}-translated`}>
      <Text variant="bodyMd" as="p" fontWeight="semibold">
        {getLanguageLabel(entry.locale)}
      </Text>
      <Text variant="bodySm" as="p" tone="subdued">
        {truncateText(entry.translated_text, 40)}
      </Text>
    </div>,
    <div key={`${entry.id}-status`}>
      {getStatusBadge(entry.job_status)}
      <Text variant="bodySm" as="p" tone="subdued">
        {formatDate(entry.created_at)}
      </Text>
    </div>,
    <ButtonGroup key={`${entry.id}-actions`}>
      <Tooltip content="View Details">
        <Button
          icon={ViewMinor}
          onClick={() => {
            setSelectedEntry(entry);
            setShowDetailModal(true);
          }}
          size="slim"
        />
      </Tooltip>
    </ButtonGroup>
  ]);

  const tabs = [
    {
      id: 'all-history',
      content: 'All History',
      accessibilityLabel: 'All translation history',
      panelID: 'all-history-panel',
    },
    {
      id: 'recent',
      content: 'Recent',
      accessibilityLabel: 'Recent translations',
      panelID: 'recent-panel',
    },
    {
      id: 'failed',
      content: 'Failed',
      accessibilityLabel: 'Failed translations',
      panelID: 'failed-panel',
    },
  ];

  const handleExport = () => {
    // TODO: Implement export functionality
  };

  return (
    <Page
      title="Translation History"
      subtitle="View detailed history of all translation activities"
      primaryAction={{
        content: 'Export History',
        onAction: handleExport,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Translation History
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Detailed audit trail of all translation activities
                  </Text>
                </TextContainer>
                
                <ButtonGroup>
                  <Button
                    icon={FilterMinor}
                    onClick={() => setShowFilters(!showFilters)}
                    variant={showFilters ? 'primary' : 'secondary'}
                  >
                    Filters
                  </Button>
                  <Button
                    icon={SearchMinor}
                    onClick={clearFilters}
                    variant="secondary"
                  >
                    Clear
                  </Button>
                </ButtonGroup>
              </div>

              {showFilters && (
                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Filter Options
                    </Text>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text variant="bodySm" as="p" fontWeight="semibold">
                          Job ID
                        </Text>
                      </div>
                      <Select
                        label=""
                        options={[
                          { label: 'All Jobs', value: '' },
                          ...jobs.map(job => ({
                            label: `Job #${job.id} - ${getResourceTypeLabel(job.resource_type)}`,
                            value: job.id.toString()
                          }))
                        ]}
                        value={filters.job_id}
                        onChange={(value) => handleFilterChange('job_id', value)}
                      />
                    </div>
                    
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text variant="bodySm" as="p" fontWeight="semibold">
                          Resource Type
                        </Text>
                      </div>
                      <Select
                        label=""
                        options={[
                          { label: 'All Resources', value: '' },
                          ...RESOURCE_TYPES
                        ]}
                        value={filters.resource_type}
                        onChange={(value) => handleFilterChange('resource_type', value)}
                      />
                    </div>
                    
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text variant="bodySm" as="p" fontWeight="semibold">
                          Language
                        </Text>
                      </div>
                      <Select
                        label=""
                        options={[
                          { label: 'All Languages', value: '' },
                          ...AVAILABLE_LANGUAGES
                        ]}
                        value={filters.locale}
                        onChange={(value) => handleFilterChange('locale', value)}
                      />
                    </div>
                    
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text variant="bodySm" as="p" fontWeight="semibold">
                          Search Text
                        </Text>
                      </div>
                      <TextField
                        label=""
                        value={filters.search}
                        onChange={(value) => handleFilterChange('search', value)}
                        placeholder="Search in text content..."
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              )}

              <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                <div style={{ marginTop: '16px' }}>
                  {filteredHistory.length > 0 ? (
                    <>
                      <DataTable
                        columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                        headings={['Job ID', 'Resource', 'Translation Key', 'Translated To', 'Status & Date', 'Actions']}
                        rows={tableRows}
                        hoverable
                      />
                      
                      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          hasPrevious={currentPage > 1}
                          onPrevious={() => setCurrentPage(currentPage - 1)}
                          hasNext={currentPage * itemsPerPage < totalItems}
                          onNext={() => setCurrentPage(currentPage + 1)}
                          label={`Page ${currentPage} of ${Math.ceil(totalItems / itemsPerPage)}`}
                        />
                      </div>
                    </>
                  ) : (
                    <EmptyState
                      heading="No translation history found"
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                    >
                      <p>No translation history entries match your current filters.</p>
                    </EmptyState>
                  )}
                </div>
              </Tabs>
            </div>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Detail Modal */}
      <Modal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Translation Details"
        primaryAction={{
          content: 'Close',
          onAction: () => setShowDetailModal(false),
        }}
      >
        <Modal.Section>
          {selectedEntry && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <Text variant="headingMd" as="h3">
                  Translation Entry #{selectedEntry.id}
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  Created on {formatDate(selectedEntry.created_at)}
                </Text>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Job Information
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Text variant="bodySm" as="p">Job ID: #{selectedEntry.job_id}</Text>
                    <Text variant="bodySm" as="p">Status: {getStatusBadge(selectedEntry.job_status)}</Text>
                    <Text variant="bodySm" as="p">Job Created: {formatDate(selectedEntry.job_created_at)}</Text>
                  </div>
                </div>
                
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Resource Information
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Text variant="bodySm" as="p">Type: {getResourceTypeLabel(selectedEntry.resource_type)}</Text>
                    <Text variant="bodySm" as="p">ID: {selectedEntry.resource_id}</Text>
                    <Text variant="bodySm" as="p">Key: {selectedEntry.translation_key}</Text>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <Text variant="bodyMd" as="p" fontWeight="semibold">
                  Translation Details
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Text variant="bodySm" as="p" fontWeight="semibold">
                    Target Language: {getLanguageLabel(selectedEntry.locale)}
                  </Text>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Original Text
                  </Text>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '12px', 
                    backgroundColor: '#f6f6f7', 
                    borderRadius: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                                     }}>
                     <div style={{ whiteSpace: 'pre-wrap' }}>
                       <Text variant="bodySm" as="p">
                         {selectedEntry.original_text}
                       </Text>
                     </div>
                   </div>
                </div>
                
                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Translated Text
                  </Text>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '12px', 
                    backgroundColor: '#f6f6f7', 
                    borderRadius: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                                     }}>
                     <div style={{ whiteSpace: 'pre-wrap' }}>
                       <Text variant="bodySm" as="p">
                         {selectedEntry.translated_text}
                       </Text>
                     </div>
                   </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}; 